import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Switch, Redirect, Router as WouterRouter, useLocation } from "wouter";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Spinner } from "@/components/ui/spinner";
import { lazy, Suspense } from "react";
import { Shell } from "@/components/layout/Shell";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const LeadDetail = lazy(() => import("@/pages/LeadDetail"));
const DiscoveryEngine = lazy(() => import("@/pages/DiscoveryEngine"));
const QuoteCockpit = lazy(() => import("@/pages/QuoteCockpit"));
const Settings = lazy(() => import("@/pages/Settings"));
const PromptHQ = lazy(() => import("@/pages/PromptHQ"));
const PromptHQAgents = lazy(() => import("@/pages/PromptHQAgents"));
const PromptHQHistory = lazy(() => import("@/pages/PromptHQHistory"));
const PromptHQSession = lazy(() => import("@/pages/PromptHQSession"));
const Login = lazy(() => import("@/pages/Login"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const NotFound = lazy(() => import("@/pages/not-found"));

function PageSpinner() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <Spinner className="size-6" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Show errors quickly instead of retrying 3× with exponential backoff.
      // Auth errors (401) are pointless to retry; network blips can be
      // retried manually via the "Try again" button in each error state.
      retry: 1,
      retryDelay: 1000,
      // Keep successful responses fresh for 60 seconds so navigating
      // back to a page doesn't re-fetch data that hasn't changed.
      staleTime: 60_000,
    },
  },
});

function AuthenticatedRouter() {
  const [location] = useLocation();
  return (
    <Shell>
      <Suspense fallback={<PageSpinner />}>
        <AnimatePresence mode="wait" initial={false}>
          <Switch key={location} location={location}>
            <Route path="/" component={Dashboard} />
            <Route path="/discovery" component={DiscoveryEngine} />
            <Route path="/cockpit" component={QuoteCockpit} />
            <Route path="/promptHQ" component={PromptHQ} />
            <Route path="/promptHQ/agents" component={PromptHQAgents} />
            <Route path="/promptHQ/history" component={PromptHQHistory} />
            <Route path="/promptHQ/sessions/:id" component={PromptHQSession} />
            <Route path="/leads/:id" component={LeadDetail} />
            <Route path="/settings" component={Settings} />
            <Route component={NotFound} />
          </Switch>
        </AnimatePresence>
      </Suspense>
    </Shell>
  );
}

function Gate() {
  const { session, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (!session) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><Spinner className="size-6" /></div>}>
        <Switch>
          <Route path="/login" component={Login} />
          <Route path="/" component={LandingPage} />
          <Route component={LandingPage} />
        </Switch>
      </Suspense>
    );
  }
  return (
    <Switch>
      <Route path="/login">
        <Redirect to="/" />
      </Route>
      <Route>
        <AuthenticatedRouter />
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <AuthProvider>
              <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                <Gate />
              </WouterRouter>
            </AuthProvider>
            <Toaster />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
